<template>
  <div class="size-consolidated-variant-wrapper grid-y">
    <ColorSwatches
      v-if="!isMattressesDivision"
      :id
      class="margin-bottom-xs"
      :color-props="{ ...colorProps, priceToColors, priceToColorsExp, disableExpandableListTransitions: true }"
      :price-to-colors="priceToColors"
      :price-to-colors-exp="priceToColorsExp"
    />

    <template v-if="isTypeDriven">
      <ProductType
        v-if="types.length"
        :id="id"
        v-model="selectedTypeId"
        :name="`cta-types-${uniqueId}-${id}`"
        :types="types"
        label="Type"
        :error-text-active
        class="margin-bottom-xs"
        @update:model-value="onTypeChange"
      />

      <SizeSelection
        :key="forceSizeRerender"
        v-model="selectedSizeId"
        v-model:touched="touched"
        v-bind="sizeProps"
        :product-id="id"
        :name="`cta-sizes-${uniqueId}-${id}`"
        :is-size-dropdown="false"
        :is-size-chart="includeSizeChart && isSizeChart"
        :size-chart-img
        :size-chart-id
        :brand-name
        :type-name
        :error-text-active
        class="margin-bottom-xs"
        @update:model-value="onSizeChange"
      />
    </template>

    <template v-else>
      <SizeSelection
        v-model="selectedSizeId"
        v-model:touched="touched"
        v-bind="sizeProps"
        :product-id="id"
        :name="`cta-sizes-${uniqueId}-${id}`"
        :is-size-dropdown="false"
        :is-size-chart="includeSizeChart && isSizeChart"
        :size-chart-img
        :size-chart-id
        :brand-name
        :type-name
        :error-text-active
        class="margin-bottom-xs"
        @update:model-value="onSizeChange"
      />
      <ProductType
        v-if="types.length"
        :id="id"
        v-model="selectedTypeId"
        :name="`cta-types-${uniqueId}-${id}`"
        :types="types"
        label="Type"
        :error-text-active
        class="margin-bottom-xs"
        @update:model-value="onTypeChange"
      />
    </template>
  </div>
</template>

<script setup>
import useAvailabilityStore from '~/stores/useAvailabilityStore';
import { ref, computed, watch, useId } from 'vue';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { SizeSelection } from '@nuxt-common/media-card-large/app/modules/size';
import { useTraitAnalyticsData } from '@nuxt-common/media-card-large/app/composables/useTraitAnalyticsData';
import { ProductType } from '@nuxt-common/media-card-large/app/modules/type';
import ColorSwatches from '~/modules/color/ColorSwatches';
import { useBrand } from '~/composables/useBrand';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useConsolidated3rdVariant from '../composables/useConsolidated3rdVariant';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';
import { useProtectionPlanAnalytics } from '~/modules/worry-no-more/analytics/useWnmAnalytics';
import useSizeChart from '~/composables/useSizeChart';
import useStickyAtbAnalytics from '~/modules/sticky-product-traits/composables/useStickyAtbAnalytics';
import { useTraitsErrorActive } from '~/modules/sticky-product-traits/composables/context-providers/useTraitsErrorActive';

const props = defineProps({
  id: {
    type: [String, Number], // root level product id -> Number; member product ids -> String; formatted -> String
    required: true,
  },
  includeSizeChart: {
    type: Boolean,
    default: true,
  },
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: true,
  },
  productPricingState: {
    type: Array,
    required: true,
  },
});

const uniqueId = useId();

const { isMcom } = useBrand();
const { brandName, typeName } = useCommonProductGetters();
const { isMattressesDivision } = useDivisionCategoryFlags();
const { getExtraData } = useProtectionPlanAnalytics();
const { extraTraitsData } = useStickyAtbAnalytics();
const { errorTextActive } = useTraitsErrorActive();
const {
  sizeProps,
  selectedSizeId,
  types,
  selectedTypeId,
  isTypeDriven,
  colorProps,
  priceToColors,
  priceToColorsExp,
  firstUpcKeyForColorAndDrivenTrait,
} = useConsolidated3rdVariant();
const { selectedUpcId } = useUpc(props.id);

const availabilityData = computed(() => useAvailabilityStore().data);
const touched = ref(false);

const { sizeChartImage: sizeChartImg, isSizeChart, sizeChartId } = useSizeChart(firstUpcKeyForColorAndDrivenTrait);

const { fireTagOnChange } = useTraitAnalyticsData({
  isConsolidatedMaster: false,
  isMcom,
  product: props.product,
  isConsolidatedThirdVariant: true,
});

const forceSizeRerender = ref(0);
if (isTypeDriven) {
  // force ExpandableList to rerender since it doesn't rerender when sizes length changes
  watch(selectedTypeId, () => {
    forceSizeRerender.value += 1;
  });
}

// We use @update:modelValue so it only listens to when user click on type
function onTypeChange() {
  fireTagOnChange({
    selectedUpcId: selectedUpcId.value,
    isVariant: !isTypeDriven,
    productPricingState: props.productPricingState,
    extraData: {
      ...getExtraData(),
      ...extraTraitsData('type'),
    },
  });
}

// We use @update:modelValue so it only listens to when user click on size
function onSizeChange() {
  setTimeout(() => {
    fireTagOnChange({
      selectedUpcId: selectedUpcId.value,
      isVariant: isTypeDriven,
      productPricingState: props.productPricingState,
      selectedSddData: availabilityData.value?.upcNumberSddMap || {},
      extraData: {
        ...getExtraData(),
        ...extraTraitsData('size'),
      },
    });
  }, 0);
}
</script>
